'use client';

import { watchItems } from '@/constants';
import { useSmoothScroller } from '@/context/SmoothScrollerContext';
import { useIntersectionObserver } from '@/lib/hooks';
import { useTranslations } from 'next-intl';
import { useEffect, useRef, useState } from 'react';
import AnimatedCard from './AnimatedCard';
import Section from './Section';

export default function WatchSection() {
    const t = useTranslations();
    // @ts-ignore
    const { lenis } = useSmoothScroller();

    // State to manage the threshold
    const [threshold, setThreshold] = useState(0.8);

    // Update threshold based on screen width
    useEffect(() => {
        const updateThreshold = () => {
            if (window.innerWidth < 1400) {
                setThreshold(1);
            } else {
                setThreshold(0.8);
            }
        };

        // Initial check
        updateThreshold();

        // Add event listener to update on resize
        window.addEventListener('resize', updateThreshold);

        // Cleanup event listener on component unmount
        return () => {
            window.removeEventListener('resize', updateThreshold);
        };
    }, []);

    // Use the updated threshold in the intersection observer
    const [sectionRef, isIntersecting] = useIntersectionObserver({ threshold });

    const scrollableRef = useRef<HTMLDivElement>(null);
    const [scrollOffset, setScrollOffset] = useState(0);

    useEffect(() => {
        if (scrollableRef.current && sectionRef.current) {
            const sectionTop = (sectionRef.current as HTMLDivElement).getBoundingClientRect().top;
            const windowHeight = window.innerHeight;

            const children = scrollableRef.current.children;
            const childWidth = children[0]?.clientWidth || 0;
            const gapWidth = 48;
            const totalWidth = (childWidth + gapWidth) * children.length;

            if (sectionTop > windowHeight) {
                setScrollOffset(0);
                scrollableRef.current.style.transform = `translateX(0px)`;
            } else {
                const initialScrollOffset = totalWidth * 0.7;
                setScrollOffset(initialScrollOffset);
                scrollableRef.current.style.transform = `translateX(-${initialScrollOffset}px)`;
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const handleScroll = (event: WheelEvent) => {
            if (!isIntersecting || !scrollableRef.current) return;

            event.preventDefault();

            const scrollDirection = event.deltaY > 0 ? 1 : -1;

            const children = scrollableRef.current.children;
            const childWidth = children[0]?.clientWidth || 0;
            const gapWidth = 48;
            const totalWidth = (childWidth + gapWidth) * children.length;

            const newScrollOffset = scrollOffset + scrollDirection * 25;
            const maxScrollOffset = totalWidth * 1.25 + 250 - scrollableRef.current.clientWidth;

            const clampedOffset = Math.max(0, Math.min(newScrollOffset, maxScrollOffset));

            setScrollOffset(clampedOffset);

            scrollableRef.current.style.transition = 'transform 1s ease-out';
            scrollableRef.current.style.transform = `translateX(-${clampedOffset}px)`;

            if (clampedOffset >= maxScrollOffset || clampedOffset === 0) {
                lenis.start();
            }
        };

        if (isIntersecting) {
            window.addEventListener('wheel', handleScroll, { passive: false });
            lenis?.stop();
        } else {
            window.removeEventListener('wheel', handleScroll);
            lenis?.start();
        }

        return () => {
            window.removeEventListener('wheel', handleScroll);
        };
    }, [isIntersecting, scrollOffset, lenis]);

    return (
        <div ref={sectionRef} className="w-full overflow-hidden">
            <Section
                id="watch"
                title={t('Header.watch')}
                className="bg-bg-200 overflow-hidden relative"
                titleClassName="bg-bg-200"
                containerClassName="overflow-hidden"
            >
                <div
                    className="w-screen pl-60 max-lg:hidden flex h-full items-center gap-12 duration-300 transition-all"
                    ref={scrollableRef}
                >
                    {watchItems(t).map(({ text, title, link, imageSrc }, index) => (
                        <AnimatedCard
                            text={text}
                            title={title}
                            link={link}
                            imageSrc={imageSrc}
                            key={index}
                        />
                    ))}
                </div>
                <div className="w-full hidden max-lg:flex flex-wrap gap-10 max-sm:flex-col max-lg:px-5 max-sm:px-0">
                    {watchItems(t).map(({ text, title, link, imageSrc }, index) => (
                        <AnimatedCard
                            text={text}
                            title={title}
                            link={link}
                            imageSrc={imageSrc}
                            key={index}
                        />
                    ))}
                </div>
            </Section>
        </div>
    );
}
